import React, { useState, useEffect } from 'react';
import { Search, Eye, MessageSquare } from 'lucide-react';
import { getOrders, updateOrderStatus, sendWhatsAppOrder } from '../../services/api';
import AdminLayout from '../../components/AdminLayout';

const AdminOrders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedOrder, setSelectedOrder] = useState(null);

    useEffect(() => {
        loadOrders();
    }, []);

    const loadOrders = async () => {
        setLoading(true);
        try {
            const res = await getOrders();
            if (res.success) setOrders(res.data);
        } catch (error) {
            console.error('Error loading orders:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (orderId, newStatus) => {
        try {
            await updateOrderStatus(orderId, newStatus);
            loadOrders();
            if (selectedOrder?.id === orderId) {
                setSelectedOrder(prev => ({ ...prev, status: newStatus }));
            }
        } catch (error) {
            console.error('Error updating order status:', error);
            alert('Failed to update order status');
        }
    };

    const handleResendWhatsApp = (order) => {
        const whatsappUrl = sendWhatsAppOrder(order);
        window.open(whatsappUrl, '_blank');
    };

    const getStatusColor = (status) => {
        const colors = {
            pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
            confirmed: 'bg-blue-100 text-blue-800 border-blue-200',
            shipped: 'bg-purple-100 text-purple-800 border-purple-200',
            delivered: 'bg-green-100 text-green-800 border-green-200',
            cancelled: 'bg-red-100 text-red-800 border-red-200',
        };
        return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
    };

    const filteredOrders = orders.filter(order => {
        const orderNumber = order.order_number || order.orderNumber || '';
        const customerName = order.customer_name || order.customerName || '';
        const customerPhone = order.customer_phone || order.customerPhone || '';

        const matchesSearch =
            orderNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
            customerName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            customerPhone.includes(searchQuery);

        const matchesStatus = statusFilter === 'all' || order.status === statusFilter;

        return matchesSearch && matchesStatus;
    });

    return (
        <AdminLayout>
            <div className="space-y-6">
                {/* Header */}
                <div>
                    <h1 className="text-3xl font-bold text-gray-900">Orders</h1>
                    <p className="text-gray-600 mt-1">Manage customer orders and track deliveries</p>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-lg shadow-md p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Search */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search by order number, customer name, or phone..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                            />
                        </div>

                        {/* Status Filter */}
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                        >
                            <option value="all">All Statuses</option>
                            <option value="pending">Pending</option>
                            <option value="confirmed">Confirmed</option>
                            <option value="shipped">Shipped</option>
                            <option value="delivered">Delivered</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                    </div>
                </div>

                {/* Orders Table */}
                <div className="bg-white rounded-lg shadow-md overflow-hidden">
                    {loading ? (
                        <div className="p-12 text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                        </div>
                    ) : filteredOrders.length === 0 ? (
                        <div className="p-12 text-center text-gray-500">
                            No orders found
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order #</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {filteredOrders.map((order) => (
                                        <tr key={order.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4">
                                                <p className="font-medium text-gray-900">{order.order_number || order.orderNumber || 'N/A'}</p>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div>
                                                    <p className="font-medium text-gray-900">{order.customer_name || order.customerName || 'Unknown'}</p>
                                                    <p className="text-sm text-gray-500">{order.customer_phone || order.customerPhone || 'N/A'}</p>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-900">{(order.items || []).length} items</td>
                                            <td className="px-6 py-4 text-sm font-medium text-gray-900">₹{(order.total_amount || order.totalAmount || 0).toLocaleString('en-IN')}</td>
                                            <td className="px-6 py-4">
                                                <select
                                                    value={order.status || 'pending'}
                                                    onChange={(e) => handleStatusChange(order.id, e.target.value)}
                                                    className={`px-3 py-1 text-xs font-medium rounded-full border ${getStatusColor(order.status || 'pending')}`}
                                                >
                                                    <option value="pending">Pending</option>
                                                    <option value="confirmed">Confirmed</option>
                                                    <option value="shipped">Shipped</option>
                                                    <option value="delivered">Delivered</option>
                                                    <option value="cancelled">Cancelled</option>
                                                </select>
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-500">
                                                {new Date(order.created_at || order.createdAt).toLocaleDateString('en-IN')}
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => setSelectedOrder(order)}
                                                        className="p-2 hover:bg-blue-50 text-blue-600 rounded transition-colors"
                                                        title="View Details"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleResendWhatsApp(order)}
                                                        className="p-2 hover:bg-green-50 text-green-600 rounded transition-colors"
                                                        title="Send WhatsApp"
                                                    >
                                                        <MessageSquare className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {/* Order Details Modal */}
            {selectedOrder && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b">
                            <h2 className="text-2xl font-bold">Order Details</h2>
                            <p className="text-gray-600">{selectedOrder.order_number || selectedOrder.orderNumber || 'N/A'}</p>
                        </div>

                        <div className="p-6 space-y-6">
                            {/* Customer Info */}
                            <div>
                                <h3 className="font-semibold text-lg mb-3">Customer Information</h3>
                                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                                    <p><span className="font-medium">Name:</span> {selectedOrder.customer_name || selectedOrder.customerName || 'Unknown'}</p>
                                    <p><span className="font-medium">Phone:</span> {selectedOrder.customer_phone || selectedOrder.customerPhone || 'N/A'}</p>
                                    {(selectedOrder.customer_email || selectedOrder.customerEmail) && (
                                        <p><span className="font-medium">Email:</span> {selectedOrder.customer_email || selectedOrder.customerEmail}</p>
                                    )}
                                    {(selectedOrder.customer_address || selectedOrder.customerAddress) && (
                                        <p><span className="font-medium">Address:</span> {selectedOrder.customer_address || selectedOrder.customerAddress}</p>
                                    )}
                                </div>
                            </div>

                            {/* Order Items */}
                            <div>
                                <h3 className="font-semibold text-lg mb-3">Order Items</h3>
                                <div className="space-y-2">
                                    {(selectedOrder.items || []).map((item, index) => (
                                        <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                                            <div>
                                                <p className="font-medium">{item.name || 'Unknown Item'}</p>
                                                <p className="text-sm text-gray-500">Quantity: {item.quantity || 0}</p>
                                            </div>
                                            <p className="font-semibold">₹{((item.price || 0) * (item.quantity || 0)).toLocaleString('en-IN')}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Order Summary */}
                            <div className="border-t pt-4">
                                <div className="flex justify-between items-center text-lg font-bold">
                                    <span>Total Amount</span>
                                    <span className="text-primary">₹{(selectedOrder.total_amount || selectedOrder.totalAmount || 0).toLocaleString('en-IN')}</span>
                                </div>
                            </div>

                            {/* Status & Notes */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Status</p>
                                    <span className={`inline-block px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(selectedOrder.status || 'pending')}`}>
                                        {selectedOrder.status || 'pending'}
                                    </span>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Order Date</p>
                                    <p className="font-medium">{new Date(selectedOrder.created_at || selectedOrder.createdAt).toLocaleString('en-IN')}</p>
                                </div>
                            </div>

                            {selectedOrder.notes && (
                                <div>
                                    <p className="text-sm text-gray-600 mb-1">Customer Notes</p>
                                    <p className="bg-gray-50 rounded-lg p-3">{selectedOrder.notes}</p>
                                </div>
                            )}
                        </div>

                        <div className="p-6 border-t flex gap-3">
                            <button
                                onClick={() => handleResendWhatsApp(selectedOrder)}
                                className="flex-1 bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                            >
                                <MessageSquare className="w-5 h-5" />
                                Send WhatsApp
                            </button>
                            <button
                                onClick={() => setSelectedOrder(null)}
                                className="flex-1 border border-gray-300 hover:bg-gray-50 py-3 rounded-lg font-medium transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default AdminOrders;
